import React, { useState, useEffect, createContext, useContext } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Star, Moon, Sun, Sparkles, MessageCircle, User, Users, Calendar, MapPin, Wand2, Crown, UserCheck, Clock, Unlock, Eye } from 'lucide-react';
import { Button } from './components/ui/button';
import { Toaster } from './components/ui/toaster';
import { useToast } from './hooks/use-toast';

// Auth Required Screen - shown when user tries to access chat without authentication
const AuthRequiredScreen = React.lazy(() => import(/* webpackChunkName: "session-chat" */ './components/AuthRequiredScreen'));

// NEW: Import AuthContext from contexts (Phase 1 refactoring)
import { AuthProvider, useAuth } from './contexts/AuthContext';
// Phase 2: Import ViewContext for navigation state management
import { ViewProvider, useView } from './contexts/ViewContext';
// Phase 3: Import UserContext for user data and kundli management
import { UserProvider, useUser } from './contexts/UserContext';
// Phase 4: Import ModalContext for global modal state (footer hiding)
import { ModalProvider } from './contexts/ModalContext';
// Phase 2B: Import UserModeContext for astrologer/seeker mode switching
import { UserModeProvider, useUserMode } from './contexts/UserModeContext';

// Re-export useAuth, useView, useUser for backward compatibility with existing components
export { useAuth } from './contexts/AuthContext';
export { useView } from './contexts/ViewContext';
export { useUser } from './contexts/UserContext';

// Configure axios to send credentials with requests
axios.defaults.withCredentials = true;

// Auth components
import LoginPage from './components/LoginPage';
import AuthCallback from './components/AuthCallback';
const OnboardingPage = React.lazy(() => import(/* webpackChunkName: "family-profiles" */ './components/OnboardingPage'));
import ProtectedRoute from './components/ProtectedRoute';
import { trackEvent, trackFunnel } from './utils/analytics';
import { resolveEntryDestination, seedIntentChat, isFreshTrialWallet, entryNudgeKey } from './utils/intentBridge';
import { parseTime } from './utils/birthTime';

const Input = ({ className, ...props }) => (
  <input 
    className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
    {...props}
  />
);

const Label = ({ className, children, ...props }) => (
  <label className={`block text-sm font-medium text-gray-700 ${className}`} {...props}>
    {children}
  </label>
);

// Import Legal Pages
const PrivacyPolicy = React.lazy(() => import(/* webpackChunkName: "legal" */ './pages/LegalPages').then((m) => ({ default: m.PrivacyPolicy })));
const TermsAndConditions = React.lazy(() => import(/* webpackChunkName: "legal" */ './pages/LegalPages').then((m) => ({ default: m.TermsAndConditions })));
const RefundPolicy = React.lazy(() => import(/* webpackChunkName: "legal" */ './pages/LegalPages').then((m) => ({ default: m.RefundPolicy })));
const ContactUs = React.lazy(() => import(/* webpackChunkName: "legal" */ './components/ContactUs'));
const AboutUs = React.lazy(() => import(/* webpackChunkName: "legal" */ './components/AboutUs'));
const BookingPage = React.lazy(() => import(/* webpackChunkName: "astrologer" */ './pages/BookingPage'));
import SeoCanonical from './components/SeoCanonical';
import AppShellFallback from './components/AppShellFallback';

// Import existing components (lazy-loaded per domain chunk for D12 4G performance)
const DivisionalCharts = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/DivisionalCharts'));
const CosmicChartViewer = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/CosmicChartViewer'));
const EnhancedDivisionalCharts = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/EnhancedDivisionalCharts'));
const VargaAshtakvargaCharts = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/VargaAshtakvargaCharts'));
const GunaMillan = React.lazy(() => import(/* webpackChunkName: "panchang" */ './components/GunaMillan'));
const PredictionsDashboard = React.lazy(() => import(/* webpackChunkName: "panchang" */ './components/PredictionsDashboard'));
const DownloadButton = React.lazy(() => import(/* webpackChunkName: "reports" */ './components/DownloadButton'));
const TransitChart = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/TransitChart'));
const DailyHoroscope = React.lazy(() => import(/* webpackChunkName: "panchang" */ './components/DailyHoroscope'));
const SubscriptionModal = React.lazy(() => import(/* webpackChunkName: "store-billing" */ './components/SubscriptionModal'));
const UpgradePrompt = React.lazy(() => import(/* webpackChunkName: "store-billing" */ './components/UpgradePrompt'));

// Import new modern components
import MobileLayout from './components/MobileLayout';
import PremiumDashboard from './components/PremiumDashboard';
import { classifySaveFailure, saveFailureMessage } from './utils/birthPlace';
const MobileDashboard = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/MobileDashboard'));
const CosmicStore = React.lazy(() => import(/* webpackChunkName: "store-billing" */ './components/CosmicStore'));
const WalletPage = React.lazy(() => import(/* webpackChunkName: "store-billing" */ './pages/WalletPage'));
const PaymentCompletePage = React.lazy(() => import(/* webpackChunkName: "store-billing" */ './pages/PaymentCompletePage'));
const ReportsPage = React.lazy(() => import(/* webpackChunkName: "reports" */ './pages/ReportsPage'));
const PredictionsPage = React.lazy(() => import(/* webpackChunkName: "panchang" */ './pages/PredictionsPage'));
const MobileKundaliView = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/MobileKundaliView'));
const MobileChartView = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/MobileChartView'));
const MobileBirthChartSwipe = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/MobileBirthChartSwipe'));
const ModernHoroscope = React.lazy(() => import(/* webpackChunkName: "panchang" */ './components/ModernHoroscope'));
const ModernProfile = React.lazy(() => import(/* webpackChunkName: "family-profiles" */ './components/ModernProfile'));
const AccountSettings = React.lazy(() => import(/* webpackChunkName: "family-profiles" */ './components/AccountSettings'));
const PurchasesAndInvoices = React.lazy(() => import(/* webpackChunkName: "family-profiles" */ './components/PurchasesAndInvoices'));
const ReportsCatalog = React.lazy(() => import(/* webpackChunkName: "reports" */ './components/ReportsCatalog'));
const ShadBalaChart = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/ShadBalaChart'));
const DashaView = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/DashaView'));
const MobileChatInterface = React.lazy(() => import(/* webpackChunkName: "session-chat" */ './components/MobileChatInterface'));
const ImprovedDateTimePicker = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/ui/ImprovedDateTimePicker'));
const AdminApp = React.lazy(() => import(/* webpackChunkName: "admin" */ './components/admin/AdminApp'));
const BirthForm = React.lazy(() => import(/* webpackChunkName: "astrologer" */ './components/workbench/BirthForm'));
import { chartStateFromCharts, saveClientProfile, fetchProfileChart, hhmm } from './utils/clientCharts';
import { chitraguptTabDestination, CHITRAGUPT_WORKBENCH_PANEL } from './utils/chitraguptRouting';
const SavedCharts = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/SavedCharts'));
const WelcomeBackModal = React.lazy(() => import(/* webpackChunkName: "family-profiles" */ './components/WelcomeBackModal'));

// Phase 2C: Client Manager (astrologer mode)
const ClientManager = React.lazy(() => import(/* webpackChunkName: "astrologer" */ './components/ClientManager'));
// Phase 2E: ChitraGupt entry card for non-subscribers
const ChitraguptEntryCard = React.lazy(() => import(/* webpackChunkName: "session-chat" */ './components/ChitraguptEntryCard'));
const SessionModeController = React.lazy(() => import(/* webpackChunkName: "session-chat" */ './components/session/SessionModeController'));
import { buildProfileSubject } from './utils/sessionSubject';
import { ActiveProfileContext } from './contexts/ActiveProfileContext';
const ChitraguptSubjectPicker = React.lazy(() => import(/* webpackChunkName: "session-chat" */ './components/session/ChitraguptSubjectPicker'));
const SavedSessionsList = React.lazy(() => import(/* webpackChunkName: "session-chat" */ './components/session/SavedSessionsList'));

// Phase 4: Import extracted components
import CosmicOnboarding from './components/CosmicOnboarding';
const ConversationalInterface = React.lazy(() => import(/* webpackChunkName: "session-chat" */ './components/ConversationalInterface'));
const KundliChart = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/KundliChart'));
const MobileBirthEntryModal = React.lazy(() => import(/* webpackChunkName: "family-profiles" */ './components/MobileBirthEntryModal'));

// Ashtakvarga Test Page for validation
const AshtakvargaTestPage = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/AshtakvargaTestPage'));
const PrashnaKundaliPage = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/PrashnaKundaliPage'));

// Unified Chart Screen with all-in-one navigation
const UnifiedChartScreen = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/UnifiedChartScreen'));

// Split Lens Chart - Innovative dual-panel chart viewer
const SplitLensChart = React.lazy(() => import(/* webpackChunkName: "kundali" */ './components/SplitLensChart'));

// Astrologer Workstation - Professional desktop multi-panel UI
const AstrologerWorkstation = React.lazy(() => import(/* webpackChunkName: "astrologer" */ './components/AstrologerWorkstation'));
const SnapshotPage = React.lazy(() => import(/* webpackChunkName: "snapshot" */ './pages/SnapshotPage'));

// 7-Day Free Trial Welcome Modal — shown once after first login
const TrialWelcomeModal = React.lazy(() => import(/* webpackChunkName: "family-profiles" */ './components/TrialWelcomeModal'));

import './App.css';
import { getBackendUrl } from './services/apiClient';
import { fetchCurrentDashaContext } from './utils/dashaUtils';

// Get API URL - this will be called at runtime for each usage
const getApiUrl = () => `${getBackendUrl()}/api`;

// Main App Component
// Shown while the active family profile's chart loads (never the owner's
// chart in its place — Batch B2), or if that load failed.
const ProfileChartLoading = ({ name, failed }) => (
  <div data-testid={failed ? 'profile-chart-failed' : 'profile-chart-loading'} style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:12,padding:32,color:'#e2d9f3'}}>
    <div style={{fontSize:32}}>✦</div>
    <p style={{fontSize:14}}>{failed ? `Couldn't load ${name ? `${name}'s` : 'this'} chart. Please try again.` : `Loading ${name ? `${name}'s` : 'the'} chart…`}</p>
  </div>
);

export function App({ userFromAuth, setUserFromAuth }) {
  const { toast } = useToast();
  
  // Check for test mode URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const testMode = urlParams.get('test') === 'true';
  const accountDeleted = urlParams.get('deleted') === 'true';
  const navigate = useNavigate();
  const location = useLocation();
  
  // Show toast if user just deleted their account
  React.useEffect(() => {
    if (accountDeleted) {
      setTimeout(() => {
        toast({
          title: "Account Deleted",
          description: "Your data has been successfully removed. Thank you for using JyotishPuraan.",
          variant: "default"
        });
        window.history.replaceState({}, document.title, '/');
      }, 500);
    }
  }, [accountDeleted, toast]);

  // Show trial activation success modal and clean URL
  const trialActivated = urlParams.get('trial_activated') === 'true';
  React.useEffect(() => {
    if (!trialActivated) return;
    // Force seeker mode — clear any stale astrologer mode from previous sessions
    try { localStorage.setItem('jp_user_mode', 'seeker'); } catch {}
    // Land on dashboard, not chat
    setCurrentView('conversational');
    setMobileView('dashboard');
    // Show congratulations modal — clear shown flag so it appears
    try { localStorage.removeItem('jp_trial_welcome_shown'); } catch {}
    setTimeout(() => {
      setShowTrialWelcome(true);
      window.history.replaceState({}, document.title, '/chitragupt');
    }, 800);
  }, [trialActivated]); // eslint-disable-line

  React.useEffect(() => {
    if (sessionStorage.getItem('app_opened_fired')) return;
    const token = localStorage.getItem('jyotish_session_token');
    if (!token) return;
    sessionStorage.setItem('app_opened_fired', 'true');
    trackEvent('app_opened', {
      platform: 'web',
      referrer: document.referrer || 'direct',
    });
  }, []);

  // Get auth context
  const auth = useAuth();
  const isAuthenticated = auth?.isAuthenticated || testMode || false;
  const authChecked = auth?.authChecked || false;
  const authLoading = auth?.authLoading || false;
  
  const viewContext = useView();
  const userContext = useUser();
  const { isAstrologerMode } = useUserMode();
  
  const justAuthenticated = auth?.justAuthenticated || location.state?.justAuthenticated || false;
  const resumeView = location.state?.resumeView;
  const userFromState = location.state?.user;
  
  // Prefer navigation state over the auth prop: after onboarding, location.state
  // carries the fresh user while the cached auth user may still be stale
  const effectiveUser = userContext?.effectiveUser || userFromState || userFromAuth;
  const hasAuthUser = effectiveUser && effectiveUser.onboarding_complete;
  
  const guestBirthDataFromSession = React.useMemo(() => {
    try {
      const data = sessionStorage.getItem('guestBirthData');
      if (!data) return null;
      if (!justAuthenticated && !location.state?.user) {
        sessionStorage.removeItem('guestBirthData');
        return null;
      }
      return JSON.parse(data);
    } catch {
      return null;
    }
  }, [justAuthenticated, location.state]);
  
  const guestBirthData = userContext?.guestBirthData || guestBirthDataFromSession;
  
  const isChatViewLocked = () => {
    const lockUntil = Number(localStorage.getItem('chat_view_lock_until') || 0);
    const isLocked = Date.now() < lockUntil;
    if (isLocked) console.log('[APP] Chat view LOCKED - preventing view changes');
    return isLocked;
  };
  
  const clearExpiredChatLock = () => {
    const lockUntil = Number(localStorage.getItem('chat_view_lock_until') || 0);
    if (lockUntil > 0 && Date.now() >= lockUntil) {
      localStorage.removeItem('chat_view_lock');
      localStorage.removeItem('chat_view_lock_until');
    }
  };

  const [showWelcomeBackModal, setShowWelcomeBackModal] = useState(false);

  // ── 7-Day Free Trial Modal state ────────────────────────────────────────
  const [showTrialWelcome, setShowTrialWelcome] = useState(false);
  
  React.useEffect(() => {
    if (!justAuthenticated && !testMode) {
      const cachedUser = localStorage.getItem('jyotish_auth_user');
      const cachedToken = localStorage.getItem('jyotish_session_token');
      if (cachedUser && cachedToken && currentView === 'onboarding') {
        try {
          const parsedUser = JSON.parse(cachedUser);
          if (parsedUser && parsedUser.date && parsedUser.time && parsedUser.place) {
            setShowWelcomeBackModal(true);
          }
        } catch (e) {
          console.warn('[APP] Failed to parse cached user');
        }
      }
    }
  }, []);
  
  const getInitialView = () => {
    if (isChatViewLocked()) return 'chat';
    clearExpiredChatLock();
    if (window.location.pathname === '/chitragupt') return 'chat';
    const cachedUserStr = localStorage.getItem('jyotish_auth_user');
    const cachedToken = localStorage.getItem('jyotish_session_token');
    if (cachedUserStr && cachedToken && !justAuthenticated) {
      try {
        const cachedUser = JSON.parse(cachedUserStr);
        if (cachedUser && cachedUser.date && cachedUser.time && cachedUser.place) return 'conversational';
      } catch (e) {}
    }
    const shouldResumeChat = sessionStorage.getItem('resumeChatAfterAuth') === 'true';
    const pendingReturnView = localStorage.getItem('pending_return_view');
    if (justAuthenticated || shouldResumeChat || pendingReturnView === 'chat') {
      sessionStorage.removeItem('resumeChatAfterAuth');
      localStorage.removeItem('pending_return_view');
      localStorage.removeItem('pending_chat_message');
      localStorage.removeItem('pending_chat_restore_in_progress');
      localStorage.removeItem('chat_view_lock');
      localStorage.removeItem('chat_view_lock_until');
    }
    const userHasCompleteBirthData = hasAuthUser && effectiveUser?.date && effectiveUser?.time && effectiveUser?.place;
    const hasGuestBirthData = guestBirthData?.date && guestBirthData?.time && guestBirthData?.place;
    if (justAuthenticated) {
      if (userHasCompleteBirthData || hasGuestBirthData) return 'conversational';
      return 'onboarding';
    }
    if (userHasCompleteBirthData) return 'conversational';
    if (effectiveUser && !effectiveUser.onboarding_complete) return 'onboarding';
    if (testMode) return 'conversational';
    if (hasGuestBirthData) return 'conversational';
    return 'onboarding';
  };
  
  const contextCurrentView = viewContext?.currentView;
  const contextMobileView = viewContext?.mobileView;
  const contextShowAuthRequired = viewContext?.showAuthRequired;
  const contextUseMobileLayout = viewContext?.useMobileLayout;
  
  const [localCurrentView, setLocalCurrentView] = useState(getInitialView());
  const [localMobileView, setLocalMobileView] = useState('dashboard');
  const [localShowAuthRequired, setLocalShowAuthRequired] = useState(false);
  
  const currentView = contextCurrentView !== undefined ? contextCurrentView : localCurrentView;
  const mobileView = contextMobileView !== undefined ? contextMobileView : localMobileView;
  const showAuthRequired = contextShowAuthRequired !== undefined ? contextShowAuthRequired : localShowAuthRequired;
  
  const setCurrentView = (value) => {
    if (viewContext?.setCurrentView) viewContext.setCurrentView(value);
    setLocalCurrentView(value);
  };
  const setMobileView = (value) => {
    if (viewContext?.setMobileView) viewContext.setMobileView(value);
    setLocalMobileView(value);
  };
  const setShowAuthRequired = (value) => {
    if (viewContext?.setShowAuthRequired) viewContext.setShowAuthRequired(value);
    setLocalShowAuthRequired(value);
  };
  
  const contextUserInfo = userContext?.userInfo;
  const contextKundliData = userContext?.kundliData;
  const contextDivisionalChartsData = userContext?.divisionalChartsData;
  
  const [localUserInfo, setLocalUserInfo] = useState(() => {
    if (hasAuthUser && effectiveUser?.date && effectiveUser?.time && effectiveUser?.place) {
      return {
        name: effectiveUser.name, gender: effectiveUser.gender,
        date: effectiveUser.date, time: effectiveUser.time, place: effectiveUser.place,
        latitude: effectiveUser.latitude, longitude: effectiveUser.longitude,
        user_id: effectiveUser.user_id, email: effectiveUser.email,
        picture: effectiveUser.picture, subscription_tier: effectiveUser.subscription_tier,
        astrologer_mode: effectiveUser.astrologer_mode || false,
        free_trial_used: effectiveUser.free_trial_used || false,
      };
    }
    if (guestBirthData) {
      return {
        name: guestBirthData.name, gender: guestBirthData.gender,
        date: guestBirthData.date, time: guestBirthData.time, place: guestBirthData.place,
        latitude: guestBirthData.latitude, longitude: guestBirthData.longitude,
      };
    }
    if (testMode) {
      let testUserId = localStorage.getItem('test_user_id');
      if (!testUserId) { testUserId = "test_user_" + Date.now(); localStorage.setItem('test_user_id', testUserId); }
      return { name: "Test User", gender: "Male", date: "03-05-1991", time: "08:05 AM", place: "Bhopal", user_id: testUserId };
    }
    return null;
  });
  const [localKundliData, setLocalKundliData] = useState(null);
  const [localDivisionalChartsData, setLocalDivisionalChartsData] = useState(null);
  
  const userInfo = contextUserInfo !== undefined ? contextUserInfo : localUserInfo;
  const kundliData = contextKundliData !== undefined ? contextKundliData : localKundliData;
  const divisionalChartsData = contextDivisionalChartsData !== undefined ? contextDivisionalChartsData : localDivisionalChartsData;
  
  const setUserInfo = (value) => {
    if (userContext?.setUserInfo) userContext.setUserInfo(value);
    setLocalUserInfo(value);
  };
  const setKundliData = (value) => {
    if (userContext?.setKundliData) userContext.setKundliData(value);
    setLocalKundliData(value);
  };
  const setDivisionalChartsData = (value) => {
    if (userContext?.setDivisionalChartsData) userContext.setDivisionalChartsData(value);
    setLocalDivisionalChartsData(value);
  };
  
  const [localLoading, setLocalLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0);
  const loading = userContext?.isChartLoading !== undefined ? userContext.isChartLoading : localLoading;
  const setLoading = setLocalLoading;

  useEffect(() => {
    let t1, t2;
    if (loading) {
      setLoadingStage(1);
      t1 = setTimeout(() => setLoadingStage(2), 2000);
      t2 = setTimeout(() => setLoadingStage(3), 4000);
    } else { setLoadingStage(0); }
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [loading]);
  
  useEffect(() => {
    if (userContext?.kundliData && localLoading) setLocalLoading(false);
  }, [userContext?.kundliData, localLoading]);
  
  // Guard to ensure login toast is shown at most once per login session
  const loginToastShownRef = React.useRef(false);

  useEffect(() => {
    if (!effectiveUser) {
      loginToastShownRef.current = false;
    }
  }, [effectiveUser]);

  // ── Auth effect: set userInfo + trigger trial modal for new users ────────
  useEffect(() => {
    const chatLocked = isChatViewLocked();
    
    if (effectiveUser && effectiveUser.onboarding_complete &&
        effectiveUser.date && effectiveUser.time && effectiveUser.place) {

      const userData = {
        name: effectiveUser.name, gender: effectiveUser.gender,
        date: effectiveUser.date, time: effectiveUser.time, place: effectiveUser.place,
        latitude: effectiveUser.latitude, longitude: effectiveUser.longitude,
        user_id: effectiveUser.user_id, email: effectiveUser.email,
        picture: effectiveUser.picture, subscription_tier: effectiveUser.subscription_tier,
        astrologer_mode: effectiveUser.astrologer_mode || false,
        free_trial_used: effectiveUser.free_trial_used || false,
      };
      setUserInfo(userData);
      
      if (chatLocked) {
        if (currentView !== 'chat') setCurrentView('chat');
        if (mobileView !== 'chat') setMobileView('chat');
        return;
      }

      if (currentView === 'onboarding') setCurrentView('conversational');
      
      sessionStorage.removeItem('resumeChatAfterAuth');
      localStorage.removeItem('pending_return_view');
      localStorage.removeItem('pending_chat_message');
      localStorage.removeItem('pending_chat_restore_in_progress');
      
      // ── Trial Welcome Modal: show once for new users, no card required ──
      if (justAuthenticated && !trialActivated) {
        // login_completed is server-owned (Analytics T2): the backend emits it
        // on OTP verification. The client must not emit it.
        const trialAlreadyShown = localStorage.getItem('jp_trial_welcome_shown');
        const trialAlreadyUsed = effectiveUser.free_trial_used || effectiveUser.trial_used;
        if (!trialAlreadyShown && !trialAlreadyUsed) {
          loginToastShownRef.current = true;
          setTimeout(() => setShowTrialWelcome(true), 1000);
        } else {
          if (!trialActivated && !loginToastShownRef.current) {
            loginToastShownRef.current = true;
            toast({ title: "Login successful ✅", description: "Welcome! Click on Chitragupt AI to start chatting." });
          }
        }
        if (currentView === 'onboarding') {
          setCurrentView('conversational');
          setMobileView('dashboard');
        }
        if (location.state?.justAuthenticated) {
          navigate(location.pathname + location.search + location.hash, {
            replace: true,
            state: { ...location.state, justAuthenticated: false },
          });
        }
      }
      
      if (!kundliData) setLoading(true);

    } else if (effectiveUser && !effectiveUser.onboarding_complete) {
      if (chatLocked) return;
      if (guestBirthData && guestBirthData.date && guestBirthData.time && guestBirthData.place) {
        setUserInfo({
          name: guestBirthData.name || effectiveUser?.name || 'User',
          gender: guestBirthData.gender, date: guestBirthData.date,
          time: guestBirthData.time, place: guestBirthData.place,
          latitude: guestBirthData.latitude, longitude: guestBirthData.longitude,
          user_id: effectiveUser?.user_id, email: effectiveUser?.email, picture: effectiveUser?.picture,
        });
        sessionStorage.removeItem('resumeChatAfterAuth');
        localStorage.removeItem('pending_return_view');
        if (justAuthenticated && !trialActivated) {
          // Show trial modal for new users who signed up via guest flow
          const trialAlreadyShown = localStorage.getItem('jp_trial_welcome_shown');
          const trialAlreadyUsed = effectiveUser?.free_trial_used || effectiveUser?.trial_used;
          if (!trialAlreadyShown && !trialAlreadyUsed) {
            loginToastShownRef.current = true;
            setTimeout(() => setShowTrialWelcome(true), 1000);
          } else {
            if (!loginToastShownRef.current) {
              loginToastShownRef.current = true;
              toast({ title: "Login successful ✅", description: "Welcome! Click on Chitragupt AI to start chatting." });
            }
          }
          setCurrentView('conversational');
          setMobileView('dashboard');
          if (location.state?.justAuthenticated) {
            navigate(location.pathname + location.search + location.hash, {
              replace: true,
              state: { ...location.state, justAuthenticated: false },
            });
          }
        }
        if (!kundliData) setLoading(true);
      } else {
        setCurrentView('onboarding');
        setLoading(false);
        sessionStorage.removeItem('resumeChatAfterAuth');
      }
    }
  }, [effectiveUser, guestBirthData, currentView, justAuthenticated, toast, location, navigate]);
  
  const requireAuth = (action) => {
    if (!isAuthenticated && !testMode) {
      toast({ title: "Sign in required", description: "Please sign in to access this feature" });
      navigate('/login');
      return false;
    }
    return true;
  };
  
  const [showNewChartModal, setShowNewChartModal] = useState(false);
  const [birthFormBusy, setBirthFormBusy] = useState(false);
  const [showMobileBirthModal, setShowMobileBirthModal] = useState(false);
  const [viewingOtherChart, setViewingOtherChart] = useState(false);
  const [originalUserInfo, setOriginalUserInfo] = useState(null);
  const [originalKundliData, setOriginalKundliData] = useState(null);
  const [familyProfiles, setFamilyProfiles] = useState([]);
  const [activeFamilyProfile, setActiveFamilyProfile] = useState(null);
  const familyReqRef = React.useRef(0);
  const [activeFamilyKundliData, setActiveFamilyKundliData] = useState(null);
  const [workstationClientInfo, setWorkstationClientInfo] = useState(null);
  const [workstationKundliData, setWorkstationKundliData] = useState(null);
  const [workstationDivisionalData, setWorkstationDivisionalData] = useState(null);
  const [showGlobalSubscriptionModal, setShowGlobalSubscriptionModal] = useState(false);
  // Karma Store overlay — opened from the header karma pill (and reusable from
  // anywhere) so top-up is reachable outside the few dashboard buttons.
  const [showCosmicStore, setShowCosmicStore] = useState(false);
  const [dashaContext, setDashaContext] = useState(null);
  
  const useMobileLayout = contextUseMobileLayout !== undefined ? contextUseMobileLayout : window.innerWidth <= 768;
  const chartCalculationAttempted = userContext?.chartCalculationAttempted || false;
  const setChartCalculationAttempted = () => {};

  // How the ChitraGupt entry screen was reached, reported on chitragupt_entry_viewed:
  // 'nav' (bottom-nav / generic), 'dashboard_card' (the dashboard CTA), or
  // 'intent_bridge' (an ad arrival). Set at every entry point below.
  const [entrySource, setEntrySource] = useState('nav');

  // Single choke-point for landing on the /chitragupt entry card: sets the view,
  // records the source for analytics, and keeps the address bar honest so a reload
  // (getInitialView reads pathname === '/chitragupt') stays on the entry screen.
  const goToChitraguptEntry = ({ source = 'nav', intent = null } = {}) => {
    setEntrySource(source);
    setCurrentView('chat');
    if (useMobileLayout) setMobileView('chat');
    try {
      const qs = intent ? `?intent=${encodeURIComponent(intent)}` : '';
      window.history.replaceState({}, document.title, `/chitragupt${qs}`);
    } catch { /* history blocked — view state already switched */ }
  };

  // ── New-signup free-first nudge (choke-point for both entry paths) ─────────
  // A plain web signup (no ?lead_id / ?intent) whose wallet holds ONLY the trial
  // karma credited at signup (purchased 0, total > 0), who has never had a
  // session and has completed onboarding with birth details, lands on the
  // /chitragupt entry card ONCE — instead of the dashboard, where the Daily
  // Prasad card sits above the ChitraGupt entry. Covers BOTH the just-onboarded
  // arrival and the post-login landing of an already-onboarded user (this effect
  // fires after routePostOnboarding has left a signal-less user on the dashboard).
  //
  // The rule is keyed on wallet state + onboarding_complete + birth details —
  // NEVER on the trial-activation call (trial karma is credited at signup,
  // before onboarding; see mobile #646/#647). Persisted per user
  // (jp_entry_nudge_shown:<user_id>) so it fires once per user, never per browser.
  const entryNudgeCheckedRef = React.useRef(false);
  useEffect(() => {
    if (entryNudgeCheckedRef.current) return;
    if (!effectiveUser) return;
    const userId = effectiveUser.user_id;
    const onboardingComplete = !!effectiveUser.onboarding_complete;
    const hasBirthDetails = !!(effectiveUser.date && effectiveUser.time && effectiveUser.place);
    if (!userId || !onboardingComplete || !hasBirthDetails) return;
    // Subscribers never see the free-first nudge.
    if (effectiveUser.has_active_subscription || (effectiveUser.subscription_tier || 0) >= 1) return;
    // Ad/intent/lead arrivals keep their own routing (routePostOnboarding + the
    // dashboard intent-bridge). Only a signal-less arrival is eligible here.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('intent') || params.get('lead_id') || sessionStorage.getItem('jp_entry_intent')) return;
    } catch { /* storage/URL blocked — proceed as signal-less */ }
    if (isChatViewLocked()) return;
    // Per-user, once. Never per browser.
    const nudgeKey = entryNudgeKey(userId);
    try { if (localStorage.getItem(nudgeKey)) return; } catch { /* storage blocked */ }

    entryNudgeCheckedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('jyotish_session_token');
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        const [walletRes, histRes] = await Promise.all([
          fetch(`${getApiUrl()}/payments/user/${userId}/wallet`, { headers }),
          fetch(`${getApiUrl()}/session/history?limit=30`, { headers, credentials: 'include' }),
        ]);
        if (cancelled || !walletRes.ok) return;
        const wallet = await walletRes.json();
        // sessionCount = real, completed readings (ended, not a cancelled
        // pre_reading) — mirrors SavedSessionsList so the count agrees.
        let sessionCount = null;
        if (histRes.ok) {
          const hist = await histRes.json();
          sessionCount = (Array.isArray(hist) ? hist : []).filter(
            (s) => s.status === 'ended' && s.end_reason !== 'cancelled'
          ).length;
        }
        const dest = resolveEntryDestination({
          freshTrialWallet: isFreshTrialWallet({
            purchasedBalance: wallet.purchased_balance,
            totalBalance: wallet.total_balance,
            sessionCount,
            onboardingComplete,
            hasBirthDetails,
          }),
        });
        if (cancelled || dest.source !== 'new_user') return;
        try { localStorage.setItem(nudgeKey, '1'); } catch { /* storage blocked */ }
        goToChitraguptEntry({ source: 'new_user' });
      } catch { /* non-fatal — the user simply stays on the dashboard */ }
    })();
    return () => { cancelled = true; };
  }, [effectiveUser]); // eslint-disable-line

  useEffect(() => {
    if (!userInfo?.date || !userInfo?.time || !userInfo?.place) return;
    fetchCurrentDashaContext(userInfo).then(ctx => { if (ctx) setDashaContext(ctx); });
  }, [userInfo?.user_id]);
  
  React.useEffect(() => {
    if (userContext?.chartCalculationAttempted || userContext?.isChartLoading) return;
    if (!userContext) {
      const hasValidBirthDetails = userInfo && userInfo.date && userInfo.time && userInfo.place;
      if ((testMode || hasAuthUser) && hasValidBirthDetails && !kundliData) {
        handleOnboardingComplete(userInfo);
      }
    }
  }, [testMode, hasAuthUser, userInfo, kundliData, userContext]);

  const handleOnboardingComplete = async (formData, { readOnly = false } = {}) => {
    setLoading(true);
    // Ad/intent arrivals (?lead_id / ?intent) land on the /chitragupt entry card
    // (or Guna Milan for matchmaking) instead of flashing the dashboard first.
    // Returns true when it routed somewhere non-default, so the caller keeps its
    // 'conversational' (dashboard) fallback only when no intent was present.
    const routePostOnboarding = () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const dest = resolveEntryDestination({
          intentParam: params.get('intent') || '',
          leadId: params.get('lead_id') || '',
          stashedIntent: sessionStorage.getItem('jp_entry_intent') || '',
        });
        if (dest.view === 'dashboard') return false;
        // Mark processed + clear the landing stash so the dashboard's own
        // intent-bridge effect can't re-fire if the user later opens it.
        try {
          if (dest.intent) sessionStorage.setItem('jp_intent_processed', dest.intent);
          sessionStorage.removeItem('jp_entry_intent');
        } catch { /* storage blocked */ }
        trackEvent('intent_bridge_fired', {
          intent: dest.intent || 'lead',
          destination: dest.view === 'chat' ? 'chitragupt_entry' : dest.view,
          source: 'onboarding',
        });
        if (dest.view === 'compatibility') { setCurrentView('compatibility'); return true; }
        // chat → the entry card, seeding the first question for career/health/marriage.
        if (dest.intent) seedIntentChat(dest.intent);
        goToChitraguptEntry({ source: 'intent_bridge', intent: dest.intent });
        return true;
      } catch { return false; }
    };
    try {
      if (!readOnly && userContext?.completeOnboarding) {
        const result = await userContext.completeOnboarding(formData);
        if (result?.saveFailed) {
          // The server did not accept the details: stop, keep the form open.
          // One toast here (every caller gets feedback); CosmicOnboarding also
          // reads the flags to re-block / "tap to retry" the place field.
          setLoading(false);
          toast.error(saveFailureMessage(result));
          return result;
        }
        if (result?.success && result?.kundliData) {
          if (!routePostOnboarding()) setCurrentView('conversational');
          setLoading(false);
          // Show trial modal for new users completing onboarding
          const trialAlreadyShown = localStorage.getItem('jp_trial_welcome_shown');
          if (!trialAlreadyShown) setTimeout(() => setShowTrialWelcome(true), 800);
          else toast.success(`Welcome ${formData.name}! Your cosmic journey begins.`);
        } else {
          setLoading(false);
          toast.error('Failed to calculate your birth chart. Please try again.');
        }
        return;
      }
      
      const parseDate = (dateInput) => {
        const trimmed = dateInput.trim().toLowerCase();
        if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(trimmed)) return trimmed;
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) return trimmed.replace(/\//g, '-');
        try {
          const p = new Date(dateInput);
          if (!isNaN(p.getTime())) return `${String(p.getDate()).padStart(2,'0')}-${String(p.getMonth()+1).padStart(2,'0')}-${p.getFullYear()}`;
        } catch {}
        return trimmed;
      };
      const parsedDate = parseDate(formData.date);
      const parsedTime = parseTime(formData.time);
      const safeName = (formData.name || '').trim() || 'User';
      const safeGender = formData.gender || 'unknown';
      const safePlace = (formData.place || '').trim();
      if (!parsedDate || !parsedTime || !safePlace) {
        toast.error('Please fill in your name, date of birth, time of birth and place of birth.');
        setLoading(false); return;
      }
      const birthDetails = { name: safeName, date: parsedDate, time: parsedTime, place: safePlace, gender: safeGender, latitude: formData.latitude, longitude: formData.longitude };
      if (!birthDetails.latitude || !birthDetails.longitude) {
        toast.error('Please select your birth place from the suggestions to get accurate coordinates.');
        setLoading(false); return;
      }
      setUserInfo(birthDetails);

      // Any failed save stops here — no chart for details the server did not
      // accept. Returned (not thrown) so callers that ignore the result
      // (MobileBirthEntryModal, the auto-calc effects) are unaffected;
      // CosmicOnboarding reads it and re-opens the place field.
      let saveFailure = null;
      if (!readOnly) {
        // JP-118: branch on the session token, never on possibly-stale context.
        // (See UserContext.completeOnboarding for the full rationale — reading
        // auth.isAuthenticated here forked into the guest branch mid-onboarding
        // and created an orphan guest_ doc after registration.)
        const sessionToken = localStorage.getItem('jyotish_session_token');
        if (auth?.isAuthenticated || sessionToken) {
          // Authenticated user: save to their own account, never create a guest doc
          try {
            const token = sessionToken;
            const resp = await axios.put(`${getApiUrl()}/auth/complete-onboarding`, birthDetails, {
              withCredentials: true,
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (resp.data?.user) {
              auth.setUser?.(resp.data.user);
              birthDetails.user_id = resp.data.user.user_id || resp.data.user.id;
              setUserInfo(birthDetails);
            }
          } catch (saveErr) { saveFailure = classifySaveFailure(saveErr); }
        } else {
          let guestUserId = localStorage.getItem('guest_user_id');
          if (!guestUserId) { guestUserId = `guest_${Date.now()}_${Math.random().toString(36).substr(2,9)}`; localStorage.setItem('guest_user_id', guestUserId); }
          try {
            await axios.post(`${getApiUrl()}/user-profile`, { user_id: guestUserId, ...birthDetails });
            birthDetails.user_id = guestUserId;
            setUserInfo(birthDetails);
          } catch (saveErr) { saveFailure = classifySaveFailure(saveErr); }
        }
      }
      if (saveFailure) {
        toast.error(saveFailureMessage(saveFailure));
        return saveFailure;
      }

      const divisionalResponse = await axios.post(`${getApiUrl()}/divisional-charts-extended`, birthDetails);
      const divisionalResponseData = divisionalResponse.data;
      if (!divisionalResponseData?.charts?.D1) throw new Error('Invalid API response');
      const d1Chart = divisionalResponseData.charts.D1;
      if (!d1Chart.planets?.length) throw new Error('Missing planets');
      const ascendantSign = d1Chart.ascendant_sign;
      if (!ascendantSign || ascendantSign < 1 || ascendantSign > 12) throw new Error(`Invalid ascendant: ${ascendantSign}`);
      const house_signs_data = d1Chart.house_signs?.length === 12
        ? d1Chart.house_signs.map((s,i) => ({house:i+1,sign_index:s,sign_name:getSignName(s)}))
        : Array.from({length:12},(_,i) => {const s=((ascendantSign-1+i)%12)+1;return{house:i+1,sign_index:s,sign_name:getSignName(s)};});
      const kundliResponseData = {
        planets: d1Chart.planets.map(p => ({...p, degrees_in_sign:Math.floor(p.degree_in_sign||0), minutes_in_sign:Math.floor(((p.degree_in_sign||0)%1)*60)})),
        ascendant_sign_index: ascendantSign, ascendant_sign_name: getSignName(ascendantSign),
        ascendant_degrees: Math.floor(d1Chart.ascendant_degree||0), ascendant_minutes: Math.floor(((d1Chart.ascendant_degree||0)%1)*60),
        ascendant_nakshatra: d1Chart.ascendant_nakshatra||'Unknown', ascendant_longitude: d1Chart.ascendant_degree||0,
        house_signs: house_signs_data, house_cusps: Array.from({length:12},(_,i)=>({house:i+1,degrees:0})),
      };
      setKundliData(kundliResponseData);
      setDivisionalChartsData(divisionalResponseData);
      
      if (kundliResponseData.planets?.length > 0) {
        if (!routePostOnboarding()) setCurrentView('conversational');
        // Show trial modal for brand new users completing onboarding form
        const trialAlreadyShown = localStorage.getItem('jp_trial_welcome_shown');
        if (!trialAlreadyShown) setTimeout(() => setShowTrialWelcome(true), 800);
        else toast.success(`Welcome ${formData.name}! Your cosmic journey begins.`);
      } else {
        toast.error('Failed to calculate your birth chart. Please try again.');
      }
    } catch (error) {
      console.error('Error in onboarding:', error);
      let msg = 'Failed to process your birth details. Please try again.';
      if (error.response) msg = `Server error ${error.response.status}: ${error.response.data?.detail || error.response.statusText}`;
      toast.error(`⚠️ ${msg}`);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (userContext?.chartCalculationAttempted) return;
    if (chartCalculationAttempted) return;
    const hasValidGuestData = guestBirthData?.date && guestBirthData?.time && guestBirthData?.place;
    if (hasValidGuestData && !kundliData && currentView === 'conversational') {
      setChartCalculationAttempted(true);
      handleOnboardingComplete(guestBirthData);
      sessionStorage.removeItem('guestBirthData');
    }
  }, [guestBirthData, currentView, kundliData, chartCalculationAttempted, userContext?.chartCalculationAttempted]);

  const getSignName = (signIndex) => {
    const signs = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'];
    return signs[signIndex - 1] || 'Unknown';
  };

  const handleViewTraditional = () => setCurrentView('traditional');
  const handleProfileUpdate = (updatedProfile) => {
    if (userContext?.updateProfile) userContext.updateProfile(updatedProfile);
    setUserInfo(prev => ({ ...prev, ...updatedProfile }));
  };
  const handleShowChart = () => {
    if (!kundliData?.planets?.length) { alert('⚠️ Chart data is missing!'); return; }
    setCurrentView('new-chart');
  };
  const handleBackToConversation = () => { setCurrentView('conversational'); setMobileView('dashboard'); };

  // The top-nav ChitraGupt tab, for an astrologer on the wide layout, opens
  // the workbench with its ChitraGupt panel instead of the seeker entry screen.
  // The counter is what the workbench reacts to; it is cleared on leaving the
  // workbench so a later visit by another route does not reopen the panel.
  const [chitraguptPanelRequest, setChitraguptPanelRequest] = useState(0);
  useEffect(() => { if (currentView !== 'workstation') setChitraguptPanelRequest(0); }, [currentView]);

  const handleMobileTabChange = (tabId) => {
    if (tabId === 'chat' && chitraguptTabDestination({
      isAstrologerMode, useMobileLayout, viewportWidth: window.innerWidth,
      chatLocked: isChatViewLocked(), hasChart: !!(workstationKundliData || kundliData),
    }) === CHITRAGUPT_WORKBENCH_PANEL) {
      setCurrentView('workstation');
      setChitraguptPanelRequest((n) => n + 1);
      return;
    }
    if (isChatViewLocked() && tabId !== 'chat') return;
    setMobileView(tabId);
    if (tabId === 'chat') { setEntrySource('nav'); setCurrentView('chat'); }
    else if (tabId === 'workstation') setCurrentView('workstation');
    else if (tabId === 'clients') setCurrentView('clients');
    else if (tabId === 'schedule') { setClientManagerInitialTab('calendar'); setCurrentView('clients'); }
    else if (tabId === 'kundali') setCurrentView('kundali');
    else if (currentView === 'chat') setCurrentView('conversational');
  };

  const handleChatNavigation = (context = null) => {
    const userIsAuthenticated = isAuthenticated || hasAuthUser || !!effectiveUser || testMode;
    if (!userIsAuthenticated) { if (context) sessionStorage.setItem('horoscopeContext', JSON.stringify(context)); setShowAuthRequired(true); return; }
    if (context) sessionStorage.setItem('horoscopeContext', JSON.stringify(context));
    setEntrySource('nav'); setCurrentView('chat'); setMobileView('chat');
  };

  // Calculate a chart from birth details without storing anything. Saved
  // clients do NOT come through here — they open by profile_id (see
  // handleViewSavedChart) so the stored coordinates are used, not the place text.
  const calculateClientChart = async (chartData) => {
    const birthDetails = { name:(chartData.name||'').trim(), date:chartData.date||chartData.dob, time:hhmm(chartData.time||chartData.tob), place:(chartData.place||chartData.pob||'').trim(), gender:chartData.gender, latitude:chartData.latitude ?? chartData.lat, longitude:chartData.longitude ?? chartData.lon };
    const divisionalResponse = await axios.post(`${getApiUrl()}/divisional-charts-extended`, birthDetails);
    return chartStateFromCharts(birthDetails, divisionalResponse.data);
  };

  // New chart (WB-12). "Calculate" shows the chart and stores nothing. "Save
  // as client" stores the profile on the server with the coordinates and time
  // zone the form resolved, then opens it by id. The browser keeps no chart list.
  const showClientChart = ({ birthDetails, kundliResult, divisionalData }) => {
    setWorkstationClientInfo(birthDetails); setWorkstationKundliData(kundliResult); setWorkstationDivisionalData(divisionalData); setViewingOtherChart(true);
    if (useMobileLayout) { setCurrentView('kundali'); setMobileView('kundali'); } else { setCurrentView('workstation'); setMobileView('dashboard'); }
  };

  const handleBirthFormCalculate = async (value) => {
    setBirthFormBusy(true);
    try {
      showClientChart(await calculateClientChart(value));
      setShowNewChartModal(false);
      trackEvent('workbench_chart_opened', { source: 'calculated' });
      toast({ title: "Chart ready", description: `${value.name}'s chart is not saved.`, duration: 2500 });
    } catch {
      toast({ title: "Error", description: "Failed to calculate the chart.", variant: "destructive" });
    } finally { setBirthFormBusy(false); }
  };

  const handleBirthFormSave = async (value) => {
    const token = localStorage.getItem('jyotish_session_token');
    if (!token) { toast({ title: "Sign in to save", description: "Use Calculate to view the chart without saving it.", variant: "destructive" }); return; }
    setBirthFormBusy(true);
    try {
      let profile;
      try {
        profile = await saveClientProfile(getApiUrl(), token, value);
      } catch (err) {
        toast({ title: "Could not save", description: err?.message || "Please try again.", variant: "destructive" });
        return;
      }
      setShowNewChartModal(false);
      try {
        showClientChart(await fetchProfileChart(getApiUrl(), token, profile.profile_id));
        trackEvent('workbench_chart_opened', { source: 'saved_new' });
        toast({ title: "Client saved", description: `Now viewing ${profile.name}'s chart.`, duration: 1500 });
      } catch {
        toast({ title: "Client saved", description: "The chart could not be opened. Open it from Clients.", variant: "destructive" });
      }
    } finally { setBirthFormBusy(false); }
  };

  const handleBackToMyChart = async () => {
    // One active-profile state (Batch B3): leaving any "other" chart also
    // leaves an active family profile — the banner used to clear while the
    // hero/Numerology/Predictions stayed on the family member.
    if (activeFamilyProfile) handleSwitchToSelf();
    setWorkstationClientInfo(null); setWorkstationKundliData(null); setWorkstationDivisionalData(null); setViewingOtherChart(false);
    if (originalUserInfo && originalKundliData) { setUserInfo(originalUserInfo); setKundliData(originalKundliData); setOriginalUserInfo(null); setOriginalKundliData(null); }
    setCurrentView('conversational'); setMobileView('dashboard');
    toast({ title: "Switched back", description: "Viewing your own birth chart", duration: 1500 });
  };

  const fetchFamilyProfileChart = React.useCallback(async (profileId) => {
    try {
      const token = localStorage.getItem('jyotish_session_token');
      const res = await fetch(`${getApiUrl()}/profiles/${profileId}/chart`, { headers: token?{Authorization:`Bearer ${token}`}:{} });
      const data = await res.json();
      return data.success && data.chart ? data.chart : null;
    } catch {
      return null;
    }
  }, []);

  const fetchFamilyProfiles = React.useCallback(async () => {
    const token = localStorage.getItem('jyotish_session_token');
    if (!token) return;
    try {
      const res = await fetch(`${getApiUrl()}/profiles`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        const filtered = (data.profiles||[]).filter(p => { const r=(p.relation_type||'').toLowerCase(); return r!=='self'&&r!=='client'; });
        setFamilyProfiles(filtered);
        const storedActiveId = localStorage.getItem('jyotish_active_profile_id');
        if (storedActiveId) {
          const matchedProfile = filtered.find(p => p.profile_id === storedActiveId);
          if (matchedProfile) {
            const reqId = ++familyReqRef.current;
            setActiveFamilyProfile(matchedProfile);
            setActiveFamilyKundliData(null);
            const chart = await fetchFamilyProfileChart(matchedProfile.profile_id);
            // Drop the response if the user switched profile meanwhile (B2).
            if (reqId === familyReqRef.current) setActiveFamilyKundliData(chart || { failed: true });
          } else {
            localStorage.removeItem('jyotish_active_profile_id');
          }
        }
      }
    } catch {}
  }, [fetchFamilyProfileChart]);

  React.useEffect(() => { if (isAuthenticated) fetchFamilyProfiles(); }, [isAuthenticated, fetchFamilyProfiles]);

  const handleSwitchToFamilyProfile = React.useCallback(async (profile, opts = {}) => {
    const reqId = ++familyReqRef.current;
    setActiveFamilyProfile(profile);
    // Never show the previous subject's chart while this one loads (B2).
    setActiveFamilyKundliData(null);
    localStorage.setItem('jyotish_active_profile_id', profile.profile_id);
    if (useMobileLayout && !opts?.stayOnHome) { setCurrentView('kundali'); setMobileView('kundali'); }
    const chart = await fetchFamilyProfileChart(profile.profile_id);
    if (reqId === familyReqRef.current) setActiveFamilyKundliData(chart || { failed: true });
  }, [useMobileLayout, fetchFamilyProfileChart]);

  const handleSwitchToSelf = React.useCallback(() => {
    familyReqRef.current += 1;   // invalidate any in-flight family chart fetch
    setActiveFamilyProfile(null);
    setActiveFamilyKundliData(null);
    setViewingOtherChart(false);
    localStorage.removeItem('jyotish_active_profile_id');
  }, []);

  // The active profile as a userInfo-shaped object (carries profile_id so every
  // profile-scoped fetch can send it). null = the account owner.
  const activeProfileUserInfo = React.useMemo(() => (activeFamilyProfile ? {
    user_id: userInfo?.user_id, profile_id: activeFamilyProfile.profile_id,
    name: activeFamilyProfile.name, gender: activeFamilyProfile.gender,
    date: activeFamilyProfile.dob, time: activeFamilyProfile.tob, place: activeFamilyProfile.pob,
    latitude: activeFamilyProfile.lat, longitude: activeFamilyProfile.lon,
  } : null), [activeFamilyProfile, userInfo?.user_id]);

  React.useEffect(() => {
    if (isAstrologerMode) {
      familyReqRef.current += 1;
      setActiveFamilyProfile(null);
      setActiveFamilyKundliData(null);
      setViewingOtherChart(false);
      localStorage.removeItem('jyotish_active_profile_id');
    }
  }, [isAstrologerMode]);

  const handleViewSavedChart = async (chart) => {
    setLoading(true);
    try {
      // A saved client opens by id: its chart comes from the coordinates and time
      // zone stored with the profile. Only a chart with no id (an old browser-only
      // entry, or a signed-out visitor) is recalculated from its birth details.
      const token = localStorage.getItem('jyotish_session_token');
      const byProfile = !!(chart.profile_id && token);
      const { birthDetails, kundliResult, divisionalData } = byProfile
        ? await fetchProfileChart(getApiUrl(), token, chart.profile_id)
        : await calculateClientChart(chart);
      trackEvent('workbench_chart_opened', { source: byProfile ? 'saved_profile' : 'recalculated' });
      setWorkstationClientInfo(birthDetails); setWorkstationKundliData(kundliResult); setWorkstationDivisionalData(divisionalData); setViewingOtherChart(true);
      if (useMobileLayout) { setCurrentView('kundali'); setMobileView('kundali'); } else { setCurrentView('workstation'); setMobileView('dashboard'); }
      toast({ title: "Chart Loaded", description: `Now viewing ${chart.name}'s chart`, duration: 1500 });
    } catch { toast({ title: "Error", description: "Failed to load client chart.", variant: "destructive" }); }
    finally { setLoading(false); }
  };

  const handleViewSavedCharts = () => setCurrentView('savedCharts');

  const handleSchedulerLoadChart = async (consultation) => {
    const client = consultation?.client || {};
    const name = client.name || consultation?.client_name || '';
    const dob = client.dob || ''; const tob = client.tob || ''; const pob = client.pob || '';
    const lat = client.lat; const lon = client.lon;
    if (!name) return;
    if (!dob || !tob || !pob || !lat || !lon) { toast({ title:"Incomplete Profile", description:`${name}'s birth details are incomplete.`, variant:"destructive" }); return; }
    setLoading(true);
    if (useMobileLayout) { setCurrentView('kundali'); setMobileView('kundali'); } else setCurrentView('workstation');
    try {
      const { birthDetails, kundliResult, divisionalData } = await calculateClientChart({ name, date:dob, time:tob, place:pob, latitude:lat, longitude:lon });
      setWorkstationClientInfo(birthDetails); setWorkstationKundliData(kundliResult); setWorkstationDivisionalData(divisionalData); setViewingOtherChart(true);
      toast({ title: "Chart Loaded", description: `Now viewing ${name}'s chart`, duration: 1500 });
    } catch { toast({ title:"Error", description:"Failed to load client chart.", variant:"destructive" }); setCurrentView('clients'); }
    finally { setLoading(false); }
  };

  const [clientManagerInitialTab, setClientManagerInitialTab] = useState('directory');

  useEffect(() => {
    if (currentView === 'schedule') { setClientManagerInitialTab('calendar'); setCurrentView('clients'); }
  }, [currentView]);

  const [karmaAccessEnabled, setKarmaAccessEnabled] = useState(false);
  const [showSessionModeFromEntry, setShowSessionModeFromEntry] = useState(false);
  // Captures the `duration_seconds` chosen on the ChitraGupt entry card so the
  // SessionPlansModal can pre-select that plan and skip the redundant duration
  // picker. Originally introduced alongside the subject picker because both
  // pieces of state are part of the same "Start session" coordination flow.
  const [entryPreselectedDuration, setEntryPreselectedDuration] = useState(null);

  // Subject picker — astrologer/seeker MUST pick whose chart this ChitraGupt
  // session is about (Self / saved client / family member) before the session
  // can begin. We hold both the chosen subject and a pending duration so the
  // flow can resume after the picker closes.
  const [sessionSubject, setSessionSubject] = useState(null);
  const [subjectPickerOpen, setSubjectPickerOpen] = useState(false);
  const [pendingSessionDuration, setPendingSessionDuration] = useState(null);

  useEffect(() => { if (currentView !== 'chat' && showSessionModeFromEntry) { setShowSessionModeFromEntry(false); setEntryPreselectedDuration(null); setSessionSubject(null); } }, [currentView]); // eslint-disable-line

  useEffect(() => { if (currentView === 'conversational' && mobileView !== 'dashboard') setMobileView('dashboard'); }, [currentView]); // eslint-disable-line

  // Build the logged-in user's own chart as a session subject.
  const buildSelfSubject = () => ({
    kind: 'self',
    profile_id: null,
    name: userInfo?.name || 'You',
    dob: userInfo?.date,
    tob: userInfo?.time,
    pob: userInfo?.place,
    lat: userInfo?.latitude,
    lon: userInfo?.longitude,
    gender: userInfo?.gender,
  });

  // Seekers should never see the "Select a Client" / subject picker on their
  // first session — default to self silently. The picker stays for astrologer
  // mode, where choosing a client is the point.
  const openSubjectPickerOrSelf = (durationSec) => {
    if (isAstrologerMode) {
      setPendingSessionDuration(durationSec || null);
      setSubjectPickerOpen(true);
      return;
    }
    // Honour an explicitly-selected family member: when one is active, seed the
    // session with that member's subject instead of silently defaulting to self,
    // so the seeker's pre-reading + chat run on the selected member's chart.
    setSessionSubject(buildProfileSubject(activeFamilyProfile) || buildSelfSubject());
    setEntryPreselectedDuration(durationSec || null);
    setShowSessionModeFromEntry(true);
  };

  const handleStartTrial = async () => {
    if (!isAuthenticated && !testMode) { setShowGlobalSubscriptionModal(true); return; }
    try {
      const token = localStorage.getItem('jyotish_session_token');
      const res = await fetch(`${getApiUrl()}/subscriptions/trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ user_id: userInfo?.user_id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.status === 'activated' || data.success)) {
        // Report the amount the backend actually credited (fall back to 599 only
        // when the response omits it) — never hardcode a promise (JP-118).
        const credited = data.karma_credited || 599;
        toast({
          title: '🌟 7-Day Free Trial Activated',
          description: `${credited} Karma credited. Pick a chart to begin your first ChitraGupt session.`,
          duration: 4500,
        });
        // Refresh karma so the entry card reflects the credited balance immediately.
        try { window.dispatchEvent(new CustomEvent('karma:refresh')); } catch {}
        setKarmaAccessEnabled(true);
        // Seekers → straight into their own first session; astrologers → picker.
        openSubjectPickerOrSelf(null);
        return;
      }
      // Already used or other error → fall back to subscription modal.
      if (res.status === 409) {
        toast({
          title: 'Trial already used',
          description: 'You\'ve already activated your free trial. Choose a plan to continue.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Could not activate trial',
          description: data?.detail || data?.message || 'Please try again.',
          variant: 'destructive',
        });
      }
      setShowGlobalSubscriptionModal(true);
    } catch (e) {
      toast({
        title: 'Network error',
        description: 'Could not reach the trial service. Please try again.',
        variant: 'destructive',
      });
      setShowGlobalSubscriptionModal(true);
    }
  };

  // ── ChitraGupt session: gate on subject selection ────────────────────────
  // Called when the user taps "Start X Min Session" on ChitraguptEntryCard.
  // Behaviour:
  //   1. If a sessionSubject is already chosen for this flow → start directly.
  //   2. Else open the picker; once confirmed, resume with the captured duration.
  const handleEntryStartWithKarma = (_planId, durationSec) => {
    if (sessionSubject) {
      setEntryPreselectedDuration(durationSec || null);
      setShowSessionModeFromEntry(true);
      return;
    }
    openSubjectPickerOrSelf(durationSec);
  };

  const handleSubjectPicked = (subj) => {
    setSessionSubject(subj);
    setSubjectPickerOpen(false);
    setEntryPreselectedDuration(pendingSessionDuration);
    setShowSessionModeFromEntry(true);
    setPendingSessionDuration(null);
    // Sync dashboard to show family member's dasha
    if (subj && subj.kind === 'profile') {
      handleSwitchToFamilyProfile(subj);
    } else if (activeFamilyProfile) {
      handleSwitchToSelf();   // a session for self: one active-profile state
    }
  };

  const handleSubjectPickerClose = () => {
    setSubjectPickerOpen(false);
    setPendingSessionDuration(null);
  };

  const handleMobileActionClick = (action) => {
    switch (action) {
      case 'conversational': setCurrentView('conversational'); setMobileView('dashboard'); break;
      case 'compatibility': setCurrentView('compatibility'); break;
      case 'download': case 'reports': handleModernNavigate('reports'); break;
      default: break;
    }
  };

  const handleModernNavigate = (destination, opts = {}) => {
    const userIsAuthenticated = auth?.isAuthenticated || hasAuthUser || !!effectiveUser || !!location.state?.user || testMode;
    if (destination === 'chat' && !userIsAuthenticated) { setShowAuthRequired(true); return; }
    proceedWithNavigation(destination, opts);
  };

  const proceedWithNavigation = (destination, opts = {}) => {
    switch (destination) {
      case 'traditional': setCurrentView('traditional'); break;
      case 'new-chart': case 'chart': setCurrentView('new-chart'); break;
      case 'kundali': setCurrentView('kundali'); if (useMobileLayout) setMobileView('kundali'); break;
      case 'splitLens': setCurrentView('splitLens'); break;
      case 'workstation':
        if (useMobileLayout && isAstrologerMode) setCurrentView(workstationClientInfo ? 'kundali' : 'clients');
        else if (useMobileLayout) setCurrentView('mobile-chart');
        else setCurrentView('workstation');
        break;
      case 'clients': setCurrentView('clients'); break;
      case 'schedule': setClientManagerInitialTab('calendar'); setCurrentView('clients'); break;
      case 'enhanced-charts': setCurrentView('enhanced-charts'); break;
      case 'varga-ashtakvarga': setCurrentView('varga-ashtakvarga'); break;
      case 'compatibility': case 'match': setCurrentView('compatibility'); break;
      case 'predictions': setCurrentView('predictions'); break;
      case 'transit': setCurrentView('transit'); break;
      case 'profile': setCurrentView('profile'); break;
      case 'dashboard': setCurrentView('conversational'); setMobileView('dashboard'); break;
      case 'horoscope': setCurrentView('horoscope'); break;
      case 'chat': goToChitraguptEntry({ source: opts.source || 'nav', intent: opts.intent || null }); break;
      case 'conversational': setCurrentView('chat'); if (useMobileLayout) setMobileView('chat'); break;
      case 'shadbala': setCurrentView('shadbala'); break;
      case 'dasha': setCurrentView('dasha'); break;
      case 'saved-sessions': setCurrentView('saved-sessions'); if (useMobileLayout) setMobileView('saved-sessions'); break;
      case 'purchases': setCurrentView('purchases'); if (useMobileLayout) setMobileView('purchases'); break;
      case 'reports': setCurrentView('conversational'); setMobileView('reports'); break;
      default: break;
    }
  };

  const shouldShowMobileBirthModal = false;

  React.useEffect(() => { if (window.location.pathname === '/admin') return; }, []);
  if (window.location.pathname === '/admin') return <AdminApp />;

  const _authPaths = ['/login', '/register', '/onboarding', '/auth/callback'];
  const hideAppChrome = _authPaths.some(p => window.location.pathname.startsWith(p));
  const shouldRenderMobileLayout = !hideAppChrome && currentView !== 'onboarding' && (currentView === 'conversational' || (currentView === 'chat' && useMobileLayout));
  const restoreInProgress = localStorage.getItem('pending_chat_restore_in_progress') === 'true';
  const chatLocked = isChatViewLocked();
  const shouldShowLoading = loading && currentView !== 'chat' && !restoreInProgress && !chatLocked;
  const hasActiveSubscription = !!(userInfo?.has_active_subscription || userInfo?.subscription_tier >= 1 || userInfo?.tier_level >= 1);

  if (showAuthRequired) {
    return (
      <React.Suspense fallback={<AppShellFallback />}>
        <AuthRequiredScreen
          onBack={() => { setShowAuthRequired(false); setCurrentView('conversational'); setMobileView('dashboard'); }}
          onSignIn={() => { sessionStorage.setItem('authReturnPath', window.location.pathname); sessionStorage.setItem('authReturnView', 'dashboard'); window.location.href = '/login'; }}
        />
      </React.Suspense>
    );
  }

  const getCachedUser = () => { try { const c = localStorage.getItem('jyotish_auth_user'); return c ? JSON.parse(c) : null; } catch { return null; } };

  return (
    <ActiveProfileContext.Provider value={activeFamilyProfile}>
    <React.Suspense fallback={<AppShellFallback />}>
    <div className="app">
      <h1 className="sr-only">JyotishPuraan — Vedic Astrology Dashboard</h1>

      {/* 7-Day Free Trial Welcome Modal */}
      {showTrialWelcome && (
        <TrialWelcomeModal
          isOpen={showTrialWelcome}
          userInfo={userInfo}
          onClose={() => setShowTrialWelcome(false)}
          onStartSession={() => {
            // Primary CTA → the free-first ChitraGupt entry card (not the dashboard),
            // so a new user goes straight to their free session.
            setShowTrialWelcome(false);
            goToChitraguptEntry({ source: 'trial_welcome' });
          }}
          onKarmaActivated={(credited) => {
            setShowTrialWelcome(false);
            // `credited` is the amount the modal confirmed the backend granted.
            toast({ title: "Trial Activated! 🌟", description: `${credited || 599} Karma credited. Begin your cosmic journey.`, duration: 5000 });
          }}
        />
      )}

      {showWelcomeBackModal && (
        <WelcomeBackModal
          isOpen={showWelcomeBackModal}
          user={getCachedUser()}
          onContinue={() => { setShowWelcomeBackModal(false); setCurrentView('conversational'); }}
          onSwitchAccount={() => { setShowWelcomeBackModal(false); trackEvent('logout', {}); localStorage.removeItem('jyotish_auth_user'); localStorage.removeItem('jyotish_session_token'); navigate('/login'); }}
          onContinueAsGuest={() => setShowWelcomeBackModal(false)}
          onClose={() => setShowWelcomeBackModal(false)}
        />
      )}

      {showMobileBirthModal && (
        <MobileBirthEntryModal isOpen={showMobileBirthModal} onClose={() => setShowMobileBirthModal(false)} onComplete={handleOnboardingComplete} initialData={userInfo} />
      )}

      {shouldShowLoading && (
        <div className="cosmic-loading">
          <div className="cosmic-background"><div className="stars-layer-1"></div><div className="stars-layer-2"></div><div className="cosmic-gradient"></div></div>
          <div className="loading-content">
            <div style={{ width:64, height:64, borderRadius:'50%', background:'radial-gradient(circle, rgba(99,102,241,0.9) 0%, rgba(139,92,246,0.4) 70%, transparent 100%)', margin:'0 auto 1.5rem', animation:'app-pulse 1.8s ease-in-out infinite' }} />
            <div style={{ minHeight:'3rem', display:'flex', flexDirection:'column', alignItems:'center', gap:'0.4rem' }}>
              <h2 className="loading-title" style={{ transition:'opacity 0.5s ease', opacity:loadingStage>0?1:0 }}>
                {loadingStage===1&&'Reading your birth stars...'}{loadingStage===2&&'Mapping planetary positions...'}{loadingStage===3&&'Preparing your cosmic portrait...'}{loadingStage===0&&'Loading...'}
              </h2>
              <p className="loading-subtitle" style={{ opacity:0.7 }}>
                {loadingStage===1&&'Analysing your moment of birth'}{loadingStage===2&&'Computing dashas & divisional charts'}{loadingStage===3&&'Almost there — this takes a few seconds'}
              </p>
            </div>
            <div style={{ display:'flex', gap:'0.5rem', justifyContent:'center', marginTop:'1.25rem' }}>
              {[1,2,3].map(s => <div key={s} style={{ width:8, height:8, borderRadius:'50%', background:loadingStage>=s?'#fbbf24':'rgba(255,255,255,0.2)', transition:'background 0.5s ease' }} />)}
            </div>
          </div>
        </div>
      )}

      {shouldRenderMobileLayout && (
        <MobileLayout activeTab={currentView==='chat'?'chat':mobileView} onTabChange={handleMobileTabChange} userInfo={userInfo} onActionClick={handleMobileActionClick} onKarmaClick={() => setShowCosmicStore(true)}>
          {!['horoscope','match','profile','settings','chat','reports','saved-sessions','purchases'].includes(mobileView) && (
            <PremiumDashboard
              userInfo={userInfo} kundliData={kundliData} onNavigate={handleModernNavigate}
              onNewChart={() => setShowNewChartModal(true)} onViewSavedCharts={handleViewSavedCharts}
              onOpenChart={handleViewSavedChart} viewingOtherChart={viewingOtherChart || !!activeFamilyProfile}
              onBackToMyChart={handleBackToMyChart} onBirthplaceSaved={handleProfileUpdate} isMobile={useMobileLayout}
              familyProfiles={familyProfiles} activeFamilyProfile={activeFamilyProfile}
              familyKundliData={activeFamilyKundliData} onSwitchToFamilyProfile={handleSwitchToFamilyProfile}
              onSwitchToSelf={handleSwitchToSelf}
              onAddFamilyProfile={(p) => { const r=(p.relation_type||'').toLowerCase(); if(r!=='self'&&r!=='client') setFamilyProfiles(prev=>[p,...prev]); }}
              onUpdateFamilyProfile={(u) => setFamilyProfiles(prev=>prev.map(p=>p.profile_id===u.profile_id?u:p))}
              onDeleteFamilyProfile={(id) => { if (activeFamilyProfile?.profile_id === id) handleSwitchToSelf(); setFamilyProfiles(prev=>prev.filter(p=>p.profile_id!==id)); }}
            />
          )}
          {mobileView==='reports' && <ReportsCatalog userInfo={userInfo} onNavigate={handleModernNavigate} activeFamilyProfile={activeFamilyProfile} />}
          {mobileView==='horoscope' && <DailyHoroscope userInfo={userInfo} userMoonSign={(() => { const s=kundliData?.planets?.find(p=>p.name==='Moon')?.sign; return s?s-1:0; })()} onBack={() => { setMobileView('home'); setCurrentView('conversational'); }} onAskChitragupt={handleChatNavigation} userTierLevel={userInfo?.tier_level||userInfo?.subscription_tier||0} onShowSubscription={() => setShowGlobalSubscriptionModal(true)} userDashaContext={dashaContext} />}
          {mobileView==='match' && <GunaMillan />}
          {mobileView==='profile' && <ModernProfile userInfo={userInfo} onNavigate={handleModernNavigate} onProfileUpdate={handleProfileUpdate} onOpenSettings={() => setMobileView('settings')} />}
          {mobileView==='settings' && <AccountSettings userInfo={userInfo} onBack={() => setMobileView('profile')} onProfileUpdate={handleProfileUpdate} onNavigate={handleModernNavigate} />}
          {mobileView==='purchases' && <PurchasesAndInvoices userInfo={userInfo} onBack={() => { setMobileView('profile'); setCurrentView('profile'); }} />}
          {mobileView==='saved-sessions' && <SavedSessionsList onClose={() => { setMobileView('home'); setCurrentView('conversational'); }} userInfo={userInfo} />}
          {currentView==='chat' && mobileView==='chat' && (
            <div key="mobile-chat-wrapper" style={{ height:'100%' }}>
              {showSessionModeFromEntry && <SessionModeController isOpen={true} onClose={() => { setShowSessionModeFromEntry(false); setEntryPreselectedDuration(null); setSessionSubject(null); }} userInfo={userInfo} autoStartWithWallet={false} preselectedDuration={entryPreselectedDuration} subject={sessionSubject} onNavigate={handleModernNavigate} />}
              {!hasActiveSubscription && !karmaAccessEnabled ? (
                <ChitraguptEntryCard userInfo={userInfo} isAuthenticated={isAuthenticated||testMode} authChecked={authChecked||testMode} hasSubscription={false} onStartTrial={handleStartTrial} onSignIn={() => { window.location.href='/login'; }} onBack={null} currentMahadasha={null} onStartWithKarma={handleEntryStartWithKarma} onBuyKarma={() => setShowGlobalSubscriptionModal(true)} subjectProfileId={activeFamilyProfile?.profile_id || null} source={entrySource} />
              ) : (
                (() => {
                  if (userInfo) return <MobileChatInterface userInfo={userInfo} onNavigate={handleModernNavigate} />;
                  if (guestBirthData) return <MobileChatInterface userInfo={{ name:guestBirthData.name||'User', gender:guestBirthData.gender, date:guestBirthData.date, time:guestBirthData.time, place:guestBirthData.place, latitude:guestBirthData.latitude, longitude:guestBirthData.longitude }} />;
                  const rip = localStorage.getItem('pending_chat_restore_in_progress')==='true';
                  const pm = localStorage.getItem('pending_chat_message');
                  if (rip||pm) return <MobileChatInterface userInfo={null} />;
                  return <div className="flex items-center justify-center h-full bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900"><div className="text-center text-white"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto mb-4"></div><p className="text-purple-200">Loading your cosmic profile...</p></div></div>;
                })()
              )}
            </div>
          )}
        </MobileLayout>
      )}

      {!shouldRenderMobileLayout && (!loading || currentView==='chat') && (
        <>
          {currentView==='onboarding' && <CosmicOnboarding onComplete={handleOnboardingComplete} isAuthenticated={isAuthenticated||testMode} />}
          {currentView==='chat' && !useMobileLayout && !hasActiveSubscription && !karmaAccessEnabled && <ChitraguptEntryCard userInfo={userInfo} isAuthenticated={isAuthenticated||testMode} authChecked={authChecked||testMode} hasSubscription={false} onStartTrial={handleStartTrial} onSignIn={() => { window.location.href='/login'; }} onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} currentMahadasha={null} onStartWithKarma={handleEntryStartWithKarma} onBuyKarma={() => setShowGlobalSubscriptionModal(true)} subjectProfileId={activeFamilyProfile?.profile_id || null} source={entrySource} />}
          {showSessionModeFromEntry && currentView==='chat' && <SessionModeController isOpen={true} onClose={() => { setShowSessionModeFromEntry(false); setEntryPreselectedDuration(null); setSessionSubject(null); }} userInfo={userInfo} autoStartWithWallet={false} preselectedDuration={entryPreselectedDuration} subject={sessionSubject} onNavigate={handleModernNavigate} />}
          {currentView==='chat' && !useMobileLayout && (hasActiveSubscription||karmaAccessEnabled) && (
            (userInfo||guestBirthData) ? (
              <div className="cosmic-interface"><div className="cosmic-background"><div className="stars-layer-1"></div><div className="stars-layer-2"></div><div className="cosmic-gradient"></div></div><div className="interface-container"><ConversationalInterface userInfo={userInfo||{name:guestBirthData?.name||'User',gender:guestBirthData?.gender,date:guestBirthData?.date,time:guestBirthData?.time,place:guestBirthData?.place,latitude:guestBirthData?.latitude,longitude:guestBirthData?.longitude}} kundliData={kundliData} divisionalChartsData={divisionalChartsData} onShowChart={handleShowChart} onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); setKarmaAccessEnabled(false); }} testMode={testMode} /></div></div>
            ) : (
              <div className="cosmic-interface"><div className="cosmic-background"><div className="stars-layer-1"></div><div className="stars-layer-2"></div><div className="cosmic-gradient"></div></div><div className="flex items-center justify-center h-screen"><div className="text-center text-white"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div><p className="text-purple-200 text-lg">Loading your cosmic profile...</p></div></div></div>
            )
          )}
          {currentView==='chart' && <CosmicChartViewer kundliData={kundliData} divisionalChartsData={divisionalChartsData} onClose={() => setCurrentView('conversational')} onBackToConversation={handleBackToConversation} userInfo={userInfo} />}
          {currentView==='new-chart' && kundliData && <UnifiedChartScreen kundliData={kundliData} divisionalChartsData={divisionalChartsData} userInfo={userInfo} onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} />}
          {currentView==='traditional' && kundliData && (
            <div className="traditional-interface">
              {(viewingOtherChart || activeFamilyProfile) && (<div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-amber-400/30 p-3 flex items-center justify-between"><div className="flex items-center space-x-2"><Eye className="w-4 h-4 text-amber-400" /><span className="text-white text-sm">Viewing chart for <span className="text-amber-400 font-medium">{activeFamilyProfile?.name||userInfo?.name}</span></span></div><button onClick={activeFamilyProfile?handleSwitchToSelf:handleBackToMyChart} className="px-3 py-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-xs font-medium">Back to My Chart</button></div>)}
              <div className="traditional-header"><Button onClick={() => setCurrentView('conversational')} variant="outline" className="back-to-chat">← Back to Dashboard</Button><div className="header-info"><h2>{(viewingOtherChart||activeFamilyProfile)?`${activeFamilyProfile?.name||userInfo?.name}'s Birth Chart`:'Complete Birth Chart Analysis'}</h2><p className="birth-details-summary">{userInfo.name} • Born {userInfo.date} at {userInfo.time} in {userInfo.place}</p><p className="chart-summary">{kundliData.ascendant_sign_name} Rising • {kundliData.planets?.length||0} Planets</p></div></div>
              {kundliData && <div className="chart-section">{!useMobileLayout && <><h3 className="chart-title">North Indian Vedic Chart</h3><KundliChart planets={kundliData.planets} ascendant_sign_index={kundliData.ascendant_sign_index} ascendant_sign_name={kundliData.ascendant_sign_name} ascendant_degrees={kundliData.ascendant_degrees} ascendant_minutes={kundliData.ascendant_minutes} ascendant_nakshatra={kundliData.ascendant_nakshatra} house_signs={kundliData.house_signs} house_cusps={kundliData.house_cusps} /></>}</div>}
            </div>
          )}
          {currentView==='enhanced-charts' && <EnhancedDivisionalCharts userInfo={activeProfileUserInfo||userInfo} isAstrologerMode={isAstrologerMode} onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} />}
          {currentView==='splitLens' && kundliData && <SplitLensChart kundliData={kundliData} divisionalChartsData={divisionalChartsData} userInfo={userInfo} onBack={() => setCurrentView('traditional')} onViewDasha={() => setCurrentView('dasha')} />}
          {currentView==='mobile-chart' && activeFamilyProfile && (activeFamilyKundliData?.charts?.D1 ? <MobileBirthChartSwipe kundliData={activeFamilyKundliData.charts.D1} divisionalChartsData={activeFamilyKundliData.charts} userInfo={activeProfileUserInfo} onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} /> : <ProfileChartLoading name={activeFamilyProfile.name} failed={!!activeFamilyKundliData?.failed} />)}
          {currentView==='mobile-chart' && !activeFamilyProfile && (kundliData ? <MobileBirthChartSwipe kundliData={kundliData} divisionalChartsData={divisionalChartsData} userInfo={userInfo} onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} /> : <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:16,padding:24}}><div style={{fontSize:40}}>✦</div><h2 style={{color:'#e2d9f3',fontSize:18,fontWeight:700,textAlign:'center'}}>Birth Details Required</h2><button onClick={() => { setCurrentView('conversational'); setShowMobileBirthModal(true); }} style={{background:'linear-gradient(135deg,#6366f1,#a855f7)',color:'#fff',border:'none',borderRadius:12,padding:'12px 28px',fontSize:14,fontWeight:600,cursor:'pointer'}}>Complete Profile</button><button onClick={() => setCurrentView('conversational')} style={{background:'none',border:'none',color:'#818CF8',fontSize:14,cursor:'pointer'}}>Go Back</button></div>)}
          {currentView==='kundali' && !workstationKundliData && activeFamilyProfile && !activeFamilyKundliData?.charts?.D1 && <ProfileChartLoading name={activeFamilyProfile.name} failed={!!activeFamilyKundliData?.failed} />}
          {currentView==='kundali' && (workstationKundliData || !activeFamilyProfile || activeFamilyKundliData?.charts?.D1) && <MobileKundaliView kundliData={workstationKundliData||(activeFamilyProfile?activeFamilyKundliData?.charts?.D1:null)||kundliData} divisionalChartsData={workstationDivisionalData||(activeFamilyProfile?activeFamilyKundliData?.charts:null)||divisionalChartsData} userInfo={workstationClientInfo||activeProfileUserInfo||userInfo} onBack={() => { if(workstationClientInfo){setWorkstationClientInfo(null);setWorkstationKundliData(null);setWorkstationDivisionalData(null);setViewingOtherChart(false);} setCurrentView('conversational');setMobileView('dashboard'); }} />}
          {currentView==='workstation' && ((workstationKundliData||kundliData)?<AstrologerWorkstation kundliData={workstationKundliData||kundliData} divisionalChartsData={workstationDivisionalData||divisionalChartsData} userInfo={workstationClientInfo||userInfo} viewingOtherChart={viewingOtherChart||!!workstationClientInfo} openChitraguptRequest={chitraguptPanelRequest} onScheduleClick={() => { setClientManagerInitialTab('calendar'); setCurrentView('clients'); }} onBack={async () => { if(viewingOtherChart||workstationClientInfo) await handleBackToMyChart(); else { setCurrentView('conversational'); setMobileView('dashboard'); } }} onNewChart={(p) => { if(p?.name&&p?.date) handleViewSavedChart(p); else setShowNewChartModal(true); }} renderNarrow={() => <MobileKundaliView kundliData={workstationKundliData||kundliData} divisionalChartsData={workstationDivisionalData||divisionalChartsData} userInfo={workstationClientInfo||userInfo} onBack={async () => { if(viewingOtherChart||workstationClientInfo) await handleBackToMyChart(); else { setCurrentView('conversational'); setMobileView('dashboard'); } }} />} />:<div data-testid="workstation-empty-state" className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white text-center p-8"><div className="text-4xl mb-4">🔭</div><h2 className="text-xl font-semibold mb-2">No Client Chart Loaded</h2><p className="text-purple-300 mb-6">Select a client from Client Manager to open their chart.</p><button data-testid="workstation-open-client-manager-btn" onClick={() => setCurrentView('clients')} className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-xl">Open Client Manager</button></div>)}
          {currentView==='clients' && <ClientManager initialTab={clientManagerInitialTab} onTabChange={setClientManagerInitialTab} onOpenChart={handleViewSavedChart} onAddChart={() => setShowNewChartModal(true)} onBack={() => { setClientManagerInitialTab('directory'); setCurrentView('conversational'); setMobileView('dashboard'); }} onLoadChart={handleSchedulerLoadChart} userInfo={userInfo} />}
          {currentView==='varga-ashtakvarga' && <VargaAshtakvargaCharts userInfo={userInfo} onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} />}
          {currentView==='compatibility' && <GunaMillan onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} />}
          {currentView==='predictions' && <PredictionsDashboard userInfo={activeProfileUserInfo||userInfo} onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} />}
          {currentView==='transit' && <TransitChart userInfo={userInfo} kundliData={kundliData} onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} onChatWithTransit={(d) => { sessionStorage.setItem('transitContext', JSON.stringify(d)); setCurrentView('chat'); }} />}
          {currentView==='horoscope' && <DailyHoroscope userInfo={userInfo} userMoonSign={(() => { const s=kundliData?.planets?.find(p=>p.name==='Moon')?.sign; return s?s-1:0; })()} onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} onAskChitragupt={handleChatNavigation} userTierLevel={userInfo?.tier_level||userInfo?.subscription_tier||0} onShowSubscription={() => setShowGlobalSubscriptionModal(true)} userDashaContext={dashaContext} />}
          {currentView==='profile' && (useMobileLayout ? <ModernProfile userInfo={userInfo} onNavigate={handleModernNavigate} onProfileUpdate={handleProfileUpdate} onOpenSettings={() => setCurrentView('settings')} /> : <AccountSettings userInfo={userInfo} onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} onProfileUpdate={handleProfileUpdate} onNavigate={handleModernNavigate} />)}
          {currentView==='settings' && <AccountSettings userInfo={userInfo} onBack={() => setCurrentView('profile')} onProfileUpdate={handleProfileUpdate} onNavigate={handleModernNavigate} />}
          {currentView==='purchases' && <PurchasesAndInvoices userInfo={userInfo} onBack={() => setCurrentView('profile')} />}
          {currentView==='shadbala' && <div className="shadbala-view"><div className="view-header"><Button onClick={() => setCurrentView('chart')} variant="outline" className="back-to-chat">← Birth Chart</Button><div className="header-info"><h2>🔋 Planetary Strength</h2><p className="view-description">ShadBala Analysis</p></div></div><ShadBalaChart userInfo={userInfo} /></div>}
          {currentView==='dasha' && userInfo && <DashaView userInfo={activeProfileUserInfo || userInfo} onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} />}
          {currentView==='saved-sessions' && <SavedSessionsList onClose={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} userInfo={userInfo} />}
        </>
      )}

      <Toaster />
      {subjectPickerOpen && (
        <ChitraguptSubjectPicker
          isOpen={subjectPickerOpen}
          onClose={handleSubjectPickerClose}
          onSelect={handleSubjectPicked}
          isAstrologerMode={isAstrologerMode}
          userInfo={userInfo}
          preselectedProfileId={workstationClientInfo?.profile_id || activeFamilyProfile?.profile_id || null}
        />
      )}
      {showNewChartModal && (
        <div data-testid="birth-form-modal" role="dialog" aria-modal="true" aria-label="New chart" style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(8,4,20,0.82)', overflowY:'auto', display:'flex', justifyContent:'center', alignItems:'flex-start', padding:'32px 16px' }}>
          <div style={{ width:'100%', maxWidth:640 }}>
            <BirthForm busy={birthFormBusy} onCalculate={handleBirthFormCalculate} onSave={handleBirthFormSave} onCancel={() => { if (!birthFormBusy) setShowNewChartModal(false); }} />
          </div>
        </div>
      )}
      {showGlobalSubscriptionModal && <SubscriptionModal userId={userInfo?.user_id} onClose={() => setShowGlobalSubscriptionModal(false)} isOpen={showGlobalSubscriptionModal} />}
      {showCosmicStore && (
        <CosmicStore
          userId={userInfo?.user_id || localStorage.getItem('guest_user_id')}
          onClose={() => setShowCosmicStore(false)}
          onPurchaseComplete={() => { try { window.dispatchEvent(new CustomEvent('karma:refresh')); } catch {} }}
        />
      )}
      {currentView==='savedCharts' && <SavedCharts onBack={() => { setCurrentView('conversational'); setMobileView('dashboard'); }} onSelectChart={handleViewSavedChart} />}
    </div>
    </React.Suspense>
    </ActiveProfileContext.Provider>
  );
}

const MainDashboard = ({ user, setUser }) => <App userFromAuth={user} setUserFromAuth={setUser} />;

const AppRouter = () => {
  const location = useLocation();
  const auth = useAuth();
  const { user: authUser, isAuthenticated: routerIsAuthenticated, authLoading: routerAuthLoading, authChecked: routerAuthChecked, justAuthenticated: routerJustAuth, handleAuthCallback, setUser, debugAuthState, hasValidSession } = auth || {};
  
  useEffect(() => {
    if (debugAuthState) debugAuthState();
  }, [routerIsAuthenticated, routerAuthChecked, routerAuthLoading, routerJustAuth, authUser, location.state?.user, debugAuthState]);
  
  useEffect(() => {
    const processAuthCallback = async () => {
      if (location.state?.justAuthenticated && location.state?.user) {
        if (handleAuthCallback) await handleAuthCallback(location.state?.sessionToken, location.state.user);
        else if (setUser && location.state.user) setUser(location.state.user);
        setTimeout(() => window.history.replaceState({}, document.title), 500);
      }
    };
    processAuthCallback();
  }, [location.state?.justAuthenticated, location.state?.user, handleAuthCallback, setUser]);

  // Fire a page_view on every route change (path only — no query string, which
  // can carry PII/tokens). The ref guards against React StrictMode's double
  // effect invocation and re-renders that don't actually change the path.
  const lastPageViewPath = React.useRef(null);
  useEffect(() => {
    const path = location.pathname;
    if (lastPageViewPath.current === path) return;
    lastPageViewPath.current = path;
    trackEvent('page_view', { path });
  }, [location.pathname]);

  const currentHash = typeof window !== 'undefined' ? window.location.hash : '';
  if (currentHash.includes('session_id=') || location.pathname === '/auth/callback') return <AuthCallback />;
  if (location.pathname.startsWith('/book/')) return <React.Suspense fallback={<AppShellFallback />}><Routes><Route path="/book/:username" element={<><SeoCanonical /><BookingPage /></>} /></Routes></React.Suspense>;

  const hasStoredUser = typeof window !== 'undefined' && !!localStorage.getItem('jyotish_auth_user');
  const hasStoredToken = typeof window !== 'undefined' && !!localStorage.getItem('jyotish_session_token');
  if (routerAuthLoading && !routerAuthChecked && !hasStoredUser && hasStoredToken) {
    return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center"><div className="text-center text-white"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-amber-500" /><p className="text-purple-200">Loading...</p></div></div>;
  }

  return (
    <React.Suspense fallback={<AppShellFallback />}>
    <Routes>
      <Route path="/auth/callback" element={<><SeoCanonical /><AuthCallback /></>} />
      <Route path="/login" element={<><SeoCanonical title="Sign In · JyotishPuraan" description="Sign in to JyotishPuraan to access your Vedic birth chart, ChitraGupt AI consultations, daily predictions, panchang and karma rewards." /><LoginPage /></>} />
      <Route path="/register" element={<><SeoCanonical title="Create Account · JyotishPuraan" description="Create your JyotishPuraan account to unlock personalised Vedic astrology readings, daily predictions and AI-guided consultations." /><LoginPage /></>} />
      <Route path="/privacy" element={<><SeoCanonical title="Privacy Policy · JyotishPuraan" description="JyotishPuraan privacy policy." /><PrivacyPolicy /></>} />
      <Route path="/terms" element={<><SeoCanonical title="Terms & Conditions · JyotishPuraan" description="Terms and conditions of use for JyotishPuraan." /><TermsAndConditions /></>} />
      <Route path="/refund" element={<><SeoCanonical title="Refund Policy · JyotishPuraan" description="Refund and cancellation policy for JyotishPuraan." /><RefundPolicy /></>} />
      <Route path="/contact" element={<><SeoCanonical title="Contact Us · JyotishPuraan" description="Reach JyotishPuraan support." /><ContactUs /></>} />
      <Route path="/about" element={<><SeoCanonical title="About · JyotishPuraan" description="JyotishPuraan combines Swiss Ephemeris precision with ChitraGupt AI." /><AboutUs /></>} />
      <Route path="/ashtakvarga" element={<><SeoCanonical title="Ashtakvarga Calculator · JyotishPuraan" description="Free Ashtakvarga calculator." /><AshtakvargaTestPage /></>} />
      <Route path="/chart-preview" element={<><SeoCanonical title="Birth Chart Preview · JyotishPuraan" description="Preview a Vedic birth chart." /><SplitLensChart kundliData={{planets:[{name:'Sun',house:12,sign:1,degree_in_sign:18.44,nakshatra:'Bharani',is_retrograde:false},{name:'Moon',house:8,sign:9,degree_in_sign:5.61,nakshatra:'Mula',is_retrograde:false},{name:'Mercury',house:11,sign:12,degree_in_sign:25.13,nakshatra:'Revati',is_retrograde:false},{name:'Venus',house:1,sign:2,degree_in_sign:29.54,nakshatra:'Mrigashira',is_retrograde:false},{name:'Mars',house:2,sign:3,degree_in_sign:22.78,nakshatra:'Punarvasu',is_retrograde:false},{name:'Jupiter',house:3,sign:4,degree_in_sign:11.52,nakshatra:'Pushya',is_retrograde:false},{name:'Saturn',house:9,sign:10,degree_in_sign:12.94,nakshatra:'Shravana',is_retrograde:false},{name:'Rahu',house:8,sign:9,degree_in_sign:27.51,nakshatra:'U.Ashadha',is_retrograde:false},{name:'Ketu',house:2,sign:3,degree_in_sign:27.51,nakshatra:'Punarvasu',is_retrograde:false}],ascendant_sign_index:2,ascendant_sign_name:'Taurus',ascendant_degrees:24,ascendant_minutes:25,ascendant_nakshatra:'Mrigashira',house_signs:[{house:1,sign_index:2,sign_name:'Taurus'},{house:2,sign_index:3,sign_name:'Gemini'},{house:3,sign_index:4,sign_name:'Cancer'},{house:4,sign_index:5,sign_name:'Leo'},{house:5,sign_index:6,sign_name:'Virgo'},{house:6,sign_index:7,sign_name:'Libra'},{house:7,sign_index:8,sign_name:'Scorpio'},{house:8,sign_index:9,sign_name:'Sagittarius'},{house:9,sign_index:10,sign_name:'Capricorn'},{house:10,sign_index:11,sign_name:'Aquarius'},{house:11,sign_index:12,sign_name:'Pisces'},{house:12,sign_index:1,sign_name:'Aries'}]}} divisionalChartsData={{charts:{D9:{ascendant_sign:5,house_signs:[5,6,7,8,9,10,11,12,1,2,3,4],planets:[{name:'Sun',house:2,sign:6,degree_in_sign:18.44},{name:'Moon',house:10,sign:2,degree_in_sign:5.61},{name:'Mercury',house:7,sign:11,degree_in_sign:25.13},{name:'Venus',house:2,sign:6,degree_in_sign:29.54},{name:'Mars',house:9,sign:1,degree_in_sign:22.78},{name:'Jupiter',house:3,sign:7,degree_in_sign:11.52},{name:'Saturn',house:9,sign:1,degree_in_sign:12.94},{name:'Rahu',house:5,sign:9,degree_in_sign:27.51},{name:'Ketu',house:11,sign:3,degree_in_sign:27.51}]}}}} userInfo={{name:'Test User',date:'03-05-1991',time:'08:05',place:'Bhopal, India'}} onBack={() => window.location.href='/'} onViewDasha={() => window.location.href='/'} /></>} />
      <Route path="/onboarding" element={<ProtectedRoute><SeoCanonical title="Onboarding · JyotishPuraan" description="Set up your JyotishPuraan profile." /><OnboardingPage /></ProtectedRoute>} />
      <Route path="/prashna" element={<ProtectedRoute><SeoCanonical title="Prashna Kundali · JyotishPuraan" description="Prashna (horary) astrology — answer a specific yes/no question from the very moment you ask, no birth time needed." /><PrashnaKundaliPage /></ProtectedRoute>} />
      <Route path="/admin/*" element={<><SeoCanonical /><AdminApp /></>} />
      <Route path="/wallet" element={<><SeoCanonical title="Wallet · JyotishPuraan" description="Your Karma wallet — check your balance and top up to unlock ChitraGupt sessions." /><WalletPage /></>} />
      <Route path="/payment-complete" element={<><SeoCanonical title="Payment Complete · JyotishPuraan" description="Confirming your JyotishPuraan payment." /><PaymentCompletePage /></>} />
      <Route path="/snapshot" element={<><SeoCanonical title="Kundali Snapshot · JyotishPuraan" description="Your daily Kundali Snapshot and today's planetary windows." /><SnapshotPage /></>} />
      <Route path="/reports" element={<><SeoCanonical title="Astrological Reports · JyotishPuraan" description="Life Guidance, Complete Astrological and Master Dharmic Blueprint PDF reports — buy with Karma or pay directly, no signup needed: just verify your mobile by OTP." /><ReportsPage /></>} />
      <Route path="/predictions" element={<><SeoCanonical title="Divine Predictions · JyotishPuraan" description="Your daily Divine Predictions — career, love, health and lucky elements, drawn from the transits shaping today." /><PredictionsPage /></>} />
      <Route path="/book/:username" element={<><SeoCanonical title="Book a Consultation · JyotishPuraan" description="Book a personal Vedic astrology consultation." /><BookingPage /></>} />
      <Route path="/*" element={<><SeoCanonical title="JyotishPuraan · Vedic Astrology Dashboard" description="Your personal Vedic astrology dashboard." /><App userFromAuth={authUser} setUserFromAuth={setUser} /></>} />
    </Routes>
    </React.Suspense>
  );
};

const AppWithAuth = () => {
  const mountedTrackedRef = React.useRef(false);
  useEffect(() => {
    if (mountedTrackedRef.current) return;
    mountedTrackedRef.current = true;
    const elapsedMs = (typeof performance !== 'undefined' && typeof performance.now === 'function')
      ? Math.round(performance.now())
      : 0;
    let chunkCount = 0;
    try {
      const scriptTags = typeof document !== 'undefined'
        ? document.querySelectorAll('script[src*="/static/js/"]').length
        : 0;
      const resourceChunks = (typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function')
        ? performance.getEntriesByType('resource').filter((r) => r && typeof r.name === 'string' && r.name.includes('/static/js/')).length
        : 0;
      chunkCount = Math.max(scriptTags, resourceChunks, 1);
    } catch {
      chunkCount = 1;
    }
    trackFunnel('app_mounted', {
      elapsed_ms: elapsedMs,
      chunk_count: chunkCount,
    });
  }, []);

  return (
    <AuthProvider><ViewProvider><UserProvider><UserModeProvider><ModalProvider><AppRouter /></ModalProvider></UserModeProvider></UserProvider></ViewProvider></AuthProvider>
  );
};

export default AppWithAuth;
